import type { ToastOptions } from "react-toastify";
import { toast } from "react-toastify";
import clsx from "clsx";

import type {
  INotifMessage,
  IPromiseNotifProps,
} from "@components/atoms/notify/types";
import "./styles.module.scss";
import {
  ErrorNotify,
  InfoNotify,
  PromiseNotify,
  SuccessNotify,
  WarningNotify,
} from "@components/atoms/notify/notif";

/**
 * Shows an informational notification.
 * @param {INotifMessage} msg - The message content for the notification.
 * @param id
 * @returns Notify - The identifier for the notification.
 */
const info = (msg: INotifMessage, id?: string) => {
  const toastOptions: ToastOptions = {
    bodyClassName: clsx("toastBody", "toastBodyInfo"),
    className: clsx("toast", "toastInfo"),
    toastId: id,
  };

  return toast.info(<InfoNotify msg={msg} />, toastOptions);
};

/**
 * Shows a warning notification.
 * @param {INotifMessage} msg - The message content for the notification.
 * @returns Notify - The identifier for the notification.
 */
const warning = (msg: INotifMessage) => {
  const toastOptions: ToastOptions = {
    bodyClassName: clsx("toastBody", "toastBodyWarning"),
    className: clsx("toast", "toastWarning"),
  };

  return toast.warning(<WarningNotify msg={msg} />, toastOptions);
};

/**
 * Shows an error notification.
 * @param {INotifMessage} msg - The message content for the notification.
 * @returns Notify - The identifier for the notification.
 */
const error = (msg: INotifMessage) => {
  const toastOptions: ToastOptions = {
    bodyClassName: clsx("toastBody", "toastBodyError"),
    className: clsx("toast", "toastError"),
  };

  return toast.error(<ErrorNotify msg={msg} />, toastOptions);
};

/**
 * Shows a success notification.
 * @param {INotifMessage} msg - The message content for the notification.
 * @returns Notify - The identifier for the notification.
 */
const success = (msg: INotifMessage) => {
  const toastOptions: ToastOptions = {
    bodyClassName: clsx("toastBody", "toastBodySuccess"),
    className: clsx("toast", "toastSuccess"),
  };

  return toast.success(<SuccessNotify msg={msg} />, toastOptions);
};

/**
 * Shows a notification for a promise-based action.
 * @param {IPromiseNotifProps} props - The properties for the promise notification.
 * @returns Promise - The promise that resolves when the notification is closed.
 */
const promise = <T = unknown,>(props: IPromiseNotifProps<T>) => {
  const { promise, pendingMessage, resolvedMessage, rejectMessage } = props;

  return toast.promise(promise, {
    success: {
      render: (data) => {
        if (resolvedMessage) {
          return (
            <SuccessNotify
              msg={
                typeof resolvedMessage === "function"
                  ? resolvedMessage(data.data)
                  : resolvedMessage
              }
            />
          );
        }
      },
      bodyClassName: clsx("toastBody", "toastBodySuccess"),
      className: clsx("toast", "toastSuccess"),
    },
    error: rejectMessage && {
      render: <ErrorNotify msg={rejectMessage} />,
      bodyClassName: clsx("toastBody", "toastBodyError"),
      className: clsx("toast", "toastError"),
    },
    pending: {
      render: <PromiseNotify msg={pendingMessage} />,
      bodyClassName: clsx("toastBody", "toastBodyInfo"),
      className: clsx("toast", "toastInfo"),
    },
  });
};

/**
 * A module that provides functions to display various types of notifications.
 */
export const notify = { info, warning, error, success, promise };
